export type AssetDocumentFile = {
  fileId: string;
  label: string;
};

export type AssetDocumentFolder = {
  name: string;
  /** Org file-library folder backing this asset folder (HWCG). */
  notefolderId?: string;
  files: AssetDocumentFile[];
};

export const DEFAULT_ASSET_DOCUMENT_FOLDERS: AssetDocumentFolder[] = [
  { name: 'Drawings', files: [] },
  { name: 'Procedures', files: [] },
];

/** Ensures Drawings and Procedures exist; keeps extras and their files. */
export function normalizeAssetDocumentFolders(
  value: unknown
): AssetDocumentFolder[] {
  const byName = new Map<string, AssetDocumentFolder>();
  if (Array.isArray(value)) {
    for (const row of value) {
      if (!row || typeof row !== 'object') continue;
      const entry = row as Record<string, unknown>;
      const name = typeof entry.name === 'string' ? entry.name.trim() : '';
      if (!name) continue;
      const files: AssetDocumentFile[] = [];
      if (Array.isArray(entry.files)) {
        for (const file of entry.files) {
          if (!file || typeof file !== 'object') continue;
          const f = file as Record<string, unknown>;
          const fileId =
            typeof f.fileId === 'string'
              ? f.fileId
              : typeof f.file_id === 'string'
                ? f.file_id
                : typeof f.id === 'string'
                  ? f.id
                  : '';
          if (!fileId) continue;
          const label =
            typeof f.label === 'string'
              ? f.label
              : typeof f.name === 'string'
                ? f.name
                : '';
          files.push({ fileId, label });
        }
      }
      const notefolderId =
        typeof entry.notefolderId === 'string' ? entry.notefolderId.trim() : '';
      byName.set(name, {
        name,
        ...(notefolderId ? { notefolderId } : {}),
        files,
      });
    }
  }
  for (const folder of DEFAULT_ASSET_DOCUMENT_FOLDERS) {
    if (!byName.has(folder.name)) {
      byName.set(folder.name, { name: folder.name, files: [] });
    }
  }
  const ordered: AssetDocumentFolder[] = [];
  for (const folder of DEFAULT_ASSET_DOCUMENT_FOLDERS) {
    ordered.push(byName.get(folder.name)!);
    byName.delete(folder.name);
  }
  for (const folder of byName.values()) {
    ordered.push(folder);
  }
  return ordered;
}

/** Static SVG used when an asset has no `image_url` yet. */
export const ASSET_IMAGE_PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360">
      <rect width="640" height="360" fill="#1f2937"/>
      <rect x="24" y="24" width="592" height="312" rx="12" fill="#374151" stroke="#4b5563"/>
      <circle cx="220" cy="140" r="36" fill="#6b7280"/>
      <path d="M80 280 L200 180 L280 240 L360 160 L560 280 Z" fill="#4b5563"/>
      <text x="320" y="310" text-anchor="middle" fill="#9ca3af" font-family="system-ui,sans-serif" font-size="18">Asset image placeholder</text>
    </svg>`
  );
